import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";

const fmt = (n) => new Intl.NumberFormat("en-AE").format(n);
const aed = (n) => `AED ${fmt(n)}`;

const KPI = ({ label, value, delta }) => (
  <div className="border border-gray-200 bg-white p-5">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{label}</div>
    <div className="mt-2 text-2xl font-black font-mono tracking-tight text-gray-900">{value}</div>
    {delta && <div className={`text-xs mt-1 font-mono ${delta.startsWith("−") || delta.startsWith("-") ? "text-emerald-700" : "text-[#0020C2]"}`}>{delta}</div>}
  </div>
);

const Badge = ({ tone, children }) => {
  const tones = {
    high:    "border-red-300 bg-red-50 text-[#DC2626]",
    medium:  "border-amber-300 bg-amber-50 text-amber-700",
    low:     "border-gray-300 bg-gray-50 text-gray-700",
    healthy: "border-emerald-300 bg-emerald-50 text-emerald-700",
    watch:   "border-amber-300 bg-amber-50 text-amber-700",
    breach:  "border-red-300 bg-red-50 text-[#DC2626]",
  };
  return <span className={`text-[10px] font-bold tracking-wider uppercase border px-1.5 py-0.5 ${tones[tone] || tones.low}`}>{children}</span>;
};

function Page({ title, eyebrow, subtitle, endpoint, render, testid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(endpoint).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [endpoint]);
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} testid={testid} />
      <div className="p-8">
        {loading ? <div className="text-sm text-gray-500">Loading…</div> :
         data    ? render(data)
                 : <div className="text-sm text-[#DC2626]">Failed to load.</div>}
      </div>
    </div>
  );
}

export function FinanceBssPage() {
  return <Page testid="telecom-finance-bss-title"
    eyebrow="Telecom Solution" title="Finance × BSS Insights"
    subtitle="One pane that joins Oracle Fusion GL with your BSS revenue ledger. Ask finance questions across both."
    endpoint="/telecom/finance-bss/kpis"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {d.kpis.map((k) => <KPI key={k.label} {...k} />)}
        </div>
        <div className="border border-gray-200 bg-white" data-testid="telecom-finance-bss-table">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Segment Join Breakdown</div>
            <h3 className="text-xl font-black tracking-tight mt-0.5">Fusion GL vs BSS Revenue</h3>
            <div className="text-[11px] font-mono text-gray-500 mt-1">Sources: {d.sources_connected.join(" · ")}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Segment", "Fusion GL (AED)", "BSS Ledger (AED)", "Gap %"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.join_breakdown.map((r) => (
                <tr key={r.segment} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{r.segment}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.fusion)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.bss)}</td>
                  <td className={`px-4 py-3 font-mono text-right font-bold ${Math.abs(r.gap_pct) > 2 ? "text-[#DC2626]" : "text-emerald-700"}`}>
                    {r.gap_pct > 0 ? "+" : ""}{r.gap_pct.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          ⓘ <strong>Demo data.</strong> Connect your BSS revenue feed (Amdocs / Comverse / Optiva) to replace these numbers with live data.
        </div>
      </div>
    )} />;
}

export function RevenueAssurancePage() {
  return <Page testid="telecom-ra-title"
    eyebrow="Telecom Solution" title="Revenue Assurance AI"
    subtitle="Automatically detects billing vs network-usage gaps and flags potential leakage in real time."
    endpoint="/telecom/revenue-assurance/alerts"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI label="Total Leakage (Apr-26)" value={aed(d.summary.total_leakage_aed)} />
          <KPI label="Open Alerts"           value={d.summary.alerts_open} />
          <KPI label="Resolved (last 90d)"   value={d.summary.alerts_resolved} />
          <KPI label="Est. Monthly Savings"  value={d.summary.estimated_monthly_savings} />
        </div>
        <div className="border border-gray-200 bg-white" data-testid="telecom-ra-table">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-black tracking-tight">Open Leakage Alerts</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Alert ID", "Rule", "Amount", "Subscribers", "Severity", "Opened"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.alerts.map((a) => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{a.id}</td>
                  <td className="px-4 py-3 font-medium">{a.rule}</td>
                  <td className="px-4 py-3 font-mono text-right">{aed(a.amount_aed)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(a.subscribers)}</td>
                  <td className="px-4 py-3"><Badge tone={a.severity}>{a.severity}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{a.opened_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )} />;
}

export function SpectrumPage() {
  return <Page testid="telecom-spectrum-title"
    eyebrow="Telecom Solution" title="Spectrum & Licence Management"
    subtitle="Inventory of every spectrum licence the operator holds, with renewal countdown and utilisation tracking."
    endpoint="/telecom/spectrum/licences"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI label="Total Licences"      value={d.summary.total_licences} />
          <KPI label="Expiring in 180 days"value={d.summary.expiring_180d} />
          <KPI label="Annual Fee"           value={aed(d.summary.annual_fee_aed)} />
          <KPI label="Avg Utilisation"     value={`${d.summary.avg_utilisation_pct}%`} />
        </div>
        <div className="border border-gray-200 bg-white" data-testid="telecom-spectrum-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Code", "Band", "Tech", "Region", "Start", "End", "Annual Fee", "Utilisation"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.licences.map((l) => {
                const today = new Date(); const end = new Date(l.end);
                const daysLeft = Math.floor((end - today) / 86400000);
                const expSoon = daysLeft < 365;
                return (
                  <tr key={l.code} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs font-bold">{l.code}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.band}</td>
                    <td className="px-4 py-3 text-xs">{l.tech}</td>
                    <td className="px-4 py-3 text-xs">{l.region}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.start}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${expSoon ? "text-[#DC2626] font-bold" : ""}`}>{l.end}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmt(l.annual_fee_aed)}</td>
                    <td className="px-4 py-3 font-mono text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-[#0020C2]" style={{ width: `${l.utilisation_pct}%` }} />
                        </div>
                        <span className="text-xs">{l.utilisation_pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="border border-blue-300 bg-blue-50 px-4 py-3 text-xs text-[#0020C2]">
          ✦ <strong>Unique to Vaku.</strong> No other ERP/BSS platform tracks spectrum licences as first-class assets. Renewal alerts hook into the existing automations engine.
        </div>
      </div>
    )} />;
}

export function SlicingPage() {
  return <Page testid="telecom-slicing-title"
    eyebrow="Telecom Solution" title="5G Network Slicing · Billing Intelligence"
    subtitle="Per-slice revenue, SLA targets vs actuals, and breach-credit exposure across every active 5G network slice."
    endpoint="/telecom/slicing/billing"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI label="Active Slices"          value={d.summary.active_slices} />
          <KPI label="Monthly Slice Revenue"  value={aed(d.summary.monthly_slice_revenue)} />
          <KPI label="SLA Breach Credits"     value={aed(d.summary.sla_breach_credits)} />
          <KPI label="Avg Utilisation"        value={`${d.summary.avg_slice_utilisation}%`} />
        </div>
        <div className="border border-gray-200 bg-white" data-testid="telecom-slicing-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Slice", "Type", "Tenant", "Subs", "Revenue (AED)", "SLA Target", "SLA Actual", "Status"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.slices.map((s) => (
                <tr key={s.slice_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{s.slice_id}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.type}</td>
                  <td className="px-4 py-3 font-medium">{s.tenant}</td>
                  <td className="px-4 py-3 font-mono text-right text-xs">{fmt(s.subscribers)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(s.revenue_aed)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.sla_target}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.sla_actual}</td>
                  <td className="px-4 py-3"><Badge tone={s.status}>{s.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-purple-300 bg-purple-50 px-4 py-3 text-xs text-purple-800">
          ⚡ <strong>3-year competitive advantage.</strong> Slice-aware billing is required for 5G SA commercialisation — Vaku is the only ERP+BSS layer that handles eMBB / URLLC / mMTC pricing natively today.
        </div>
      </div>
    )} />;
}
